import { reducedMotion } from "./motion_pref";

// stat numbers roll from the old value to the new one. only purely numeric text is touched, the final text is
// written exactly at the end and the element is aria-busy while it rolls so readers hear the settled value once.

const STATS = ".meter-num, .rank-score, #ach-points, #ach-rank, #budget-total, #leaders-score, #jp-streak, #jp-count-fresh, #jp-count-learning, #jp-count-review, #oc-count, [data-countup]";
const NUM = /^(\D{0,2})(-?\d[\d,]*(?:\.(\d+))?)(\s?(?:%|pts?|x)?)$/;
const MS = 500;

type Roll = { from: number; to: number; at: number; pre: string; post: string; dec: number; group: boolean; final: string; cur: number; shown: string };
const rolls = new Map<HTMLElement, Roll>();
const seen = new WeakMap<HTMLElement, number>();
let running = false;

const parse = (text: string) => {
  const m = NUM.exec(text.trim());
  return m ? { pre: m[1], value: parseFloat(m[2].replace(/,/g, "")), dec: m[3]?.length ?? 0, group: m[2].includes(","), post: m[4] } : null;
};

function write(el: HTMLElement, text: string): void {
  el.textContent = text;
}

function tick(now: number): void {
  running = false;
  rolls.forEach((r, el) => {
    const t = Math.min(1, (now - r.at) / MS);
    if (t >= 1 || !el.isConnected) {
      write(el, r.final);
      el.removeAttribute("aria-busy");
      el.classList.remove("rolling");
      rolls.delete(el);
      return;
    }
    const v = r.from + (r.to - r.from) * (1 - Math.pow(1 - t, 3));
    const body = r.group ? v.toLocaleString("en-US", { minimumFractionDigits: r.dec, maximumFractionDigits: r.dec }) : v.toFixed(r.dec);
    r.cur = v;
    r.shown = r.pre + body + r.post;
    write(el, r.shown);
  });
  if (rolls.size) { running = true; requestAnimationFrame(tick); }
}

function changed(el: HTMLElement): void {
  const own = rolls.get(el);
  if (own && el.textContent === own.shown) return;
  const next = parse(el.textContent ?? "");
  const active = rolls.get(el);
  if (!next) { seen.delete(el); if (active) { rolls.delete(el); el.removeAttribute("aria-busy"); el.classList.remove("rolling"); } return; }
  const known = seen.get(el);
  seen.set(el, next.value);
  const from = active ? active.cur : known ?? 0;
  if (reducedMotion() || document.documentElement.classList.contains("nojuice") || from === next.value || document.hidden) {
    if (active) { rolls.delete(el); el.removeAttribute("aria-busy"); el.classList.remove("rolling"); }
    return;
  }
  rolls.set(el, { from, to: next.value, at: performance.now(), pre: next.pre, post: next.post, dec: next.dec, group: next.group, final: el.textContent ?? "", cur: from, shown: "" });
  el.setAttribute("aria-busy", "true");
  el.classList.add("rolling");
  if (!running) { running = true; requestAnimationFrame(tick); }
}

export function setupCountUp(): void {
  new MutationObserver((records) => {
    const hit = new Set<HTMLElement>();
    for (const r of records) {
      const node = r.target;
      const el = node.nodeType === 3 ? node.parentElement : (node as HTMLElement);
      if (el?.matches?.(STATS)) hit.add(el);
    }
    hit.forEach(changed);
  }).observe(document.body, { childList: true, characterData: true, subtree: true });
}
